import { useState, useEffect, useRef } from "react";

/* ─────────────────────────────────────────────────────────
   KANNAVISCARE — State-of-the-art white luxury medical
   Aesthetic: Swiss editorial × Italian luxury × medical precision
   Inspired by: La Prairie, Vogue Italia, private Swiss clinics
   Fonts: Freight Display Pro (editorial serif) + Suisse Int'l (precision sans)
   Colors: Pure white + deep emerald #1B4332 + gold #B8975A + ink #0D0D0D
───────────────────────────────────────────────────────── */

const IMG = {
  hero:     "https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=1800&q=90&fit=crop&crop=center",
  about:    "https://images.unsplash.com/photo-1559757175-5700dde675bc?w=1200&q=85&fit=crop&crop=center",
  step1:    "https://images.unsplash.com/photo-1586015555751-63bb77f4322a?w=800&q=85&fit=crop",
  step2:    "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800&q=85&fit=crop",
  step3:    "https://images.unsplash.com/photo-1587614382346-4ec70e388b28?w=800&q=85&fit=crop",
  doctor1:  "https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=600&h=800&q=85&fit=crop&crop=faces",
  doctor2:  "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=600&h=800&q=85&fit=crop&crop=faces",
  doctor3:  "https://images.unsplash.com/photo-1622253692010-333f2da6031d?w=600&h=800&q=85&fit=crop&crop=faces",
  cta:      "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=1800&q=85&fit=crop&crop=center",
  texture:  "https://images.unsplash.com/photo-1557683311-eac922347aa1?w=1200&q=60&fit=crop",
};

/* ── CSS ─────────────────────────────────────────────── */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400;1,500&family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;1,300;1,400&family=Outfit:wght@300;400;500;600&display=swap');

/* ── RESET & BASE ── */
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
html{scroll-behavior:smooth;font-size:16px}
body{
  font-family:'Outfit',sans-serif;
  background:#FAFAF8;
  color:#0D0D0D;
  overflow-x:hidden;
  -webkit-font-smoothing:antialiased;
  -moz-osx-font-smoothing:grayscale;
}
::selection{background:#1B4332;color:#fff}
img{display:block;width:100%;height:100%;object-fit:cover}
button,input,select{font-family:'Outfit',sans-serif}
a{text-decoration:none;color:inherit}
::-webkit-scrollbar{width:2px}
::-webkit-scrollbar-thumb{background:#1B4332}

/* ── CSS VARIABLES ── */
:root{
  --white: #FFFFFF;
  --off:   #FAFAF8;
  --ink:   #0D0D0D;
  --ink60: rgba(13,13,13,.6);
  --ink35: rgba(13,13,13,.35);
  --ink12: rgba(13,13,13,.12);
  --ink06: rgba(13,13,13,.06);
  --emerald:    #1B4332;
  --emerald-md: #2D6A4F;
  --emerald-lt: #52B788;
  --gold:       #B8975A;
  --gold-lt:    #D4AF7A;
  --cream:      #F5EFE6;
}

/* ── KEYFRAMES ── */
@keyframes fadeUp{from{opacity:0;transform:translateY(48px)}to{opacity:1;transform:translateY(0)}}
@keyframes fadeIn{from{opacity:0}to{opacity:1}}
@keyframes slideInLeft{from{opacity:0;transform:translateX(-60px)}to{opacity:1;transform:translateX(0)}}
@keyframes scaleIn{from{opacity:0;transform:scale(.94)}to{opacity:1;transform:scale(1)}}
@keyframes lineGrow{from{transform:scaleX(0)}to{transform:scaleX(1)}}
@keyframes numberCount{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
@keyframes shimmer{0%{background-position:200%}100%{background-position:-200%}}
@keyframes scrollDrop{0%{top:0;opacity:1}70%{top:100%;opacity:0}71%{top:0;opacity:0}100%{top:0;opacity:1}}
@keyframes marqueeLTR{from{transform:translateX(0)}to{transform:translateX(-50%)}}

/* ── NAVIGATION ── */
.nav{
  position:fixed;top:0;left:0;right:0;z-index:300;
  height:80px;
  display:flex;align-items:center;justify-content:space-between;
  padding:0 60px;
  transition:all .5s cubic-bezier(.16,1,.3,1);
}
.nav::after{
  content:'';
  position:absolute;bottom:0;left:60px;right:60px;
  height:1px;background:var(--ink12);
  transform:scaleX(0);transform-origin:left;
  transition:transform .6s .2s ease;
}
.nav.solid{
  height:68px;
  background:rgba(250,250,248,.96);
  backdrop-filter:blur(20px) saturate(180%);
  -webkit-backdrop-filter:blur(20px) saturate(180%);
  box-shadow:0 1px 0 var(--ink06);
}
.nav.solid::after{transform:scaleX(1)}
.nav-logo{
  display:flex;align-items:center;gap:12px;
  font-family:'Cormorant Garamond',serif;
  font-size:17px;font-weight:500;letter-spacing:3px;
  text-transform:uppercase;color:var(--ink);
}
.nav-logo-icon{
  width:34px;height:34px;border-radius:50%;
  border:1.5px solid var(--emerald);
  display:flex;align-items:center;justify-content:center;
  font-size:13px;
  background:var(--emerald);color:#fff;
  letter-spacing:0;font-weight:500;
  font-family:'Outfit',sans-serif;font-size:11px;
}
.nav-links{
  position:absolute;left:50%;transform:translateX(-50%);
  display:flex;gap:48px;list-style:none;
}
.nav-links a{
  font-size:11px;letter-spacing:2.5px;text-transform:uppercase;
  color:var(--ink60);font-weight:400;
  position:relative;transition:color .3s;
}
.nav-links a::after{
  content:'';position:absolute;bottom:-4px;left:0;right:0;
  height:1px;background:var(--emerald);
  transform:scaleX(0);transform-origin:left;
  transition:transform .4s ease;
}
.nav-links a:hover{color:var(--ink)}
.nav-links a:hover::after{transform:scaleX(1)}
.nav-right{display:flex;align-items:center;gap:20px}
.lang-switch{
  display:flex;gap:0;
  border:1px solid var(--ink12);border-radius:2px;
  overflow:hidden;
}
.lang-switch button{
  padding:6px 14px;font-size:10px;letter-spacing:2px;
  text-transform:uppercase;background:transparent;
  border:none;color:var(--ink60);cursor:pointer;
  transition:all .25s;font-family:'Outfit',sans-serif;
}
.lang-switch button.on{background:var(--emerald);color:#fff}
.nav-book{
  padding:10px 28px;
  background:var(--emerald);color:#fff;
  font-size:10px;letter-spacing:2.5px;text-transform:uppercase;
  border:none;cursor:pointer;
  border-radius:2px;
  transition:all .3s;font-family:'Outfit',sans-serif;
}
.nav-book:hover{background:var(--emerald-md);box-shadow:0 8px 24px rgba(27,67,50,.3)}

/* ── HERO ── */
.hero{
  height:100vh;min-height:700px;
  display:grid;grid-template-columns:1fr 1fr;
  overflow:hidden;position:relative;
  padding-top:80px;
}
.hero-left{
  display:flex;flex-direction:column;justify-content:center;
  padding:80px 72px 80px 60px;
  background:var(--off);
  position:relative;z-index:2;
}
.hero-left::after{
  content:'';
  position:absolute;right:-1px;top:15%;bottom:15%;
  width:1px;background:var(--ink12);
}
.hero-eyebrow{
  display:flex;align-items:center;gap:12px;
  margin-bottom:36px;
  opacity:0;animation:fadeUp .6s .2s ease forwards;
}
.hero-eyebrow-line{width:32px;height:1px;background:var(--emerald)}
.hero-eyebrow-text{
  font-size:10px;letter-spacing:3px;text-transform:uppercase;
  color:var(--emerald);font-weight:500;
}
.hero-h1{
  font-family:'Cormorant',serif;
  font-size:clamp(52px,5.5vw,86px);
  font-weight:300;line-height:1.04;
  letter-spacing:-1px;
  color:var(--ink);
  margin-bottom:32px;
  opacity:0;animation:fadeUp .8s .35s ease forwards;
}
.hero-h1 em{
  font-style:italic;font-weight:300;
  color:var(--emerald);
}
.hero-h1 strong{
  font-weight:500;
  background:linear-gradient(90deg,var(--gold),var(--gold-lt),var(--gold));
  background-size:200%;
  -webkit-background-clip:text;-webkit-text-fill-color:transparent;
  background-clip:text;
  animation:shimmer 4s linear infinite;
}
.hero-sub{
  font-size:15px;font-weight:300;line-height:1.8;
  color:var(--ink60);max-width:440px;
  margin-bottom:52px;
  opacity:0;animation:fadeUp .8s .5s ease forwards;
}
.hero-actions{
  display:flex;align-items:center;gap:16px;
  opacity:0;animation:fadeUp .8s .65s ease forwards;
}
.btn-primary{
  display:inline-flex;align-items:center;gap:12px;
  padding:16px 40px;
  background:var(--emerald);color:#fff;
  font-size:11px;letter-spacing:2px;text-transform:uppercase;
  font-weight:500;border:none;cursor:pointer;border-radius:2px;
  transition:all .35s;position:relative;overflow:hidden;
}
.btn-primary::before{
  content:'';position:absolute;inset:0;
  background:var(--emerald-md);
  transform:translateX(-101%);transition:transform .4s cubic-bezier(.16,1,.3,1);
}
.btn-primary:hover::before{transform:translateX(0)}
.btn-primary:hover{box-shadow:0 16px 48px rgba(27,67,50,.35)}
.btn-primary span,.btn-primary i{position:relative;z-index:1}
.btn-secondary{
  display:inline-flex;align-items:center;gap:8px;
  padding:16px 0;
  background:transparent;border:none;
  font-size:11px;letter-spacing:2px;text-transform:uppercase;
  color:var(--ink60);cursor:pointer;
  border-bottom:1px solid var(--ink12);
  transition:all .3s;
}
.btn-secondary:hover{color:var(--ink);border-color:var(--ink)}

.hero-right{
  position:relative;overflow:hidden;
}
.hero-right-img{
  position:absolute;inset:0;
  transform:scale(1.06);
  transition:transform 10s ease;
}
.hero-right-img.loaded{transform:scale(1)}
.hero-right-img img{width:100%;height:100%}
.hero-right-overlay{
  position:absolute;inset:0;
  background:linear-gradient(135deg,rgba(27,67,50,.15) 0%,transparent 60%);
}
/* floating card */
.hero-card{
  position:absolute;bottom:48px;left:-28px;
  background:#fff;
  border:1px solid var(--ink06);
  border-radius:3px;
  padding:28px 32px;
  box-shadow:0 24px 80px rgba(0,0,0,.12);
  min-width:240px;
  opacity:0;animation:slideInLeft 1s .9s cubic-bezier(.16,1,.3,1) forwards;
}
.hero-card-num{
  font-family:'Cormorant',serif;
  font-size:48px;font-weight:300;color:var(--ink);line-height:1;
}
.hero-card-num span{font-size:24px;color:var(--gold)}
.hero-card-label{font-size:11px;letter-spacing:1.5px;text-transform:uppercase;color:var(--ink60);margin-top:6px}
.hero-card-sub{font-size:12px;color:var(--ink35);margin-top:4px}
/* scroll indicator */
.hero-scroll{
  position:absolute;bottom:48px;left:50%;transform:translateX(-50%);
  display:flex;flex-direction:column;align-items:center;gap:10px;
  z-index:10;opacity:0;animation:fadeIn 1s 1.4s ease forwards;
}
.hero-scroll span{font-size:9px;letter-spacing:3px;text-transform:uppercase;color:var(--ink35)}
.hero-scroll-track{
  width:1px;height:48px;background:var(--ink12);
  position:relative;overflow:hidden;
}
.hero-scroll-dot{
  position:absolute;width:1px;top:0;background:var(--emerald);
  animation:scrollDrop 2.2s ease infinite;
}

/* ── MARQUEE STRIP ── */
.marquee-strip{
  background:var(--emerald);
  padding:14px 0;overflow:hidden;
  border-top:1px solid rgba(255,255,255,.1);
}
.marquee-inner{
  display:flex;gap:0;width:max-content;
  animation:marqueeLTR 25s linear infinite;
}
.marquee-item{
  display:flex;align-items:center;gap:20px;
  padding:0 40px;
  font-size:10px;letter-spacing:3px;text-transform:uppercase;
  color:rgba(255,255,255,.7);font-weight:400;white-space:nowrap;
}
.marquee-dot{width:3px;height:3px;border-radius:50%;background:var(--gold);flex-shrink:0}

/* ── SECTION BASE ── */
.section{padding:140px 60px}
.section-sm{padding:100px 60px}
.s-label{
  display:flex;align-items:center;gap:14px;
  font-size:10px;letter-spacing:3px;text-transform:uppercase;
  color:var(--emerald);font-weight:500;margin-bottom:20px;
}
.s-label::before{content:'';width:28px;height:1px;background:var(--emerald)}
.s-h2{
  font-family:'Cormorant',serif;
  font-size:clamp(40px,4vw,66px);
  font-weight:300;line-height:1.08;
  color:var(--ink);
  letter-spacing:-.5px;
}
.s-h2 em{font-style:italic;color:var(--emerald)}
.s-lead{
  font-size:16px;font-weight:300;line-height:1.8;
  color:var(--ink60);max-width:520px;
}

/* ── ABOUT / INTRO ── */
.about-grid{
  display:grid;grid-template-columns:7fr 5fr;
  gap:0;border:1px solid var(--ink06);
  border-radius:4px;overflow:hidden;
}
.about-left{
  padding:80px;
  border-right:1px solid var(--ink06);
  display:flex;flex-direction:column;justify-content:center;
}
.about-p{
  font-size:15px;font-weight:300;line-height:1.9;
  color:var(--ink60);margin-top:28px;margin-bottom:48px;
  max-width:520px;
}
.about-certifications{
  display:flex;flex-direction:column;gap:16px;
  margin-top:auto;padding-top:48px;
  border-top:1px solid var(--ink06);
}
.about-cert{
  display:flex;align-items:center;gap:14px;
  font-size:12px;font-weight:400;color:var(--ink60);
}
.about-cert-icon{
  width:32px;height:32px;border-radius:50%;
  background:rgba(27,67,50,.08);
  display:flex;align-items:center;justify-content:center;
  font-size:14px;flex-shrink:0;
}
.about-right{
  position:relative;overflow:hidden;min-height:560px;
  background:var(--cream);
}
.about-right img{position:absolute;inset:0;width:100%;height:100%}
.about-badge{
  position:absolute;bottom:0;left:0;right:0;
  background:var(--emerald);
  padding:28px 36px;
  display:flex;align-items:baseline;gap:16px;
}
.about-badge-num{
  font-family:'Cormorant',serif;
  font-size:40px;font-weight:300;color:#fff;line-height:1;
}
.about-badge-text{font-size:12px;letter-spacing:1.5px;text-transform:uppercase;color:rgba(255,255,255,.65)}

/* ── STATS BAND ── */
.stats-band{
  background:var(--ink);
  display:grid;grid-template-columns:repeat(4,1fr);
  border-top:1px solid rgba(255,255,255,.06);
}
.stat-item{
  padding:64px 52px;
  border-right:1px solid rgba(255,255,255,.08);
  position:relative;
}
.stat-item:last-child{border-right:none}
.stat-item::before{
  content:'';position:absolute;top:0;left:0;right:0;
  height:2px;background:linear-gradient(90deg,var(--emerald-lt),var(--gold));
  transform:scaleX(0);transform-origin:left;
  transition:transform .6s ease;
}
.stat-item:hover::before{transform:scaleX(1)}
.stat-n{
  font-family:'Cormorant',serif;
  font-size:clamp(44px,4vw,68px);
  font-weight:300;color:#fff;line-height:1;
  margin-bottom:12px;
}
.stat-n span{font-size:.5em;color:var(--gold);vertical-align:super;margin-left:2px}
.stat-label{font-size:11px;letter-spacing:2px;text-transform:uppercase;color:rgba(255,255,255,.4);margin-bottom:6px}
.stat-sub{font-size:13px;color:rgba(255,255,255,.3);font-weight:300}

/* ── HOW IT WORKS ── */
.how-section{background:#fff;border-top:1px solid var(--ink06)}
.how-header{
  display:grid;grid-template-columns:1fr 1fr;
  gap:60px;align-items:flex-end;margin-bottom:100px;
}
.how-steps{
  display:grid;grid-template-columns:repeat(3,1fr);
  gap:1px;background:var(--ink06);
  border:1px solid var(--ink06);border-radius:4px;overflow:hidden;
}
.how-step{
  background:#fff;
  position:relative;overflow:hidden;
  transition:background .4s;
}
.how-step:hover{background:rgba(27,67,50,.03)}
.how-step-img{height:260px;position:relative;overflow:hidden}
.how-step-img img{transition:transform .7s ease}
.how-step:hover .how-step-img img{transform:scale(1.05)}
.how-step-img-overlay{
  position:absolute;inset:0;
  background:linear-gradient(180deg,transparent 50%,rgba(255,255,255,.4) 100%);
}
.how-step-body{padding:40px}
.how-step-num{
  font-family:'Cormorant',serif;
  font-size:11px;letter-spacing:3px;
  color:var(--emerald);margin-bottom:18px;font-weight:500;
}
.how-step-title{
  font-family:'Cormorant',serif;
  font-size:26px;font-weight:400;
  color:var(--ink);margin-bottom:14px;line-height:1.2;
}
.how-step-desc{
  font-size:14px;font-weight:300;line-height:1.85;
  color:var(--ink60);
}
.how-step-cta{
  display:inline-flex;align-items:center;gap:8px;
  margin-top:24px;font-size:11px;letter-spacing:2px;
  text-transform:uppercase;color:var(--emerald);
  border-bottom:1px solid transparent;
  transition:border-color .3s;padding-bottom:2px;cursor:pointer;
}
.how-step:hover .how-step-cta{border-color:var(--emerald)}

/* ── SERVICES ── */
.services-section{background:var(--off)}
.services-header{
  display:grid;grid-template-columns:1fr 1fr;
  gap:60px;align-items:flex-end;margin-bottom:72px;
}
.services-grid{
  display:grid;grid-template-columns:repeat(2,1fr);
  gap:1px;background:var(--ink06);
  border:1px solid var(--ink06);border-radius:4px;overflow:hidden;
}
.service-card{
  background:#fff;padding:56px 52px;
  position:relative;overflow:hidden;
  cursor:pointer;transition:all .4s;
}
.service-card::after{
  content:'';position:absolute;
  left:0;bottom:0;top:0;width:3px;
  background:linear-gradient(180deg,var(--emerald),var(--emerald-lt));
  transform:scaleY(0);transform-origin:bottom;
  transition:transform .5s cubic-bezier(.16,1,.3,1);
}
.service-card:hover{background:rgba(27,67,50,.02)}
.service-card:hover::after{transform:scaleY(1)}
.service-num{
  font-family:'Cormorant',serif;
  font-size:72px;font-weight:300;
  color:var(--ink06);
  position:absolute;top:24px;right:36px;
  line-height:1;
  transition:color .4s;
}
.service-card:hover .service-num{color:rgba(27,67,50,.06)}
.service-tag{
  font-size:10px;letter-spacing:2.5px;text-transform:uppercase;
  color:var(--emerald);font-weight:500;margin-bottom:16px;
}
.service-title{
  font-family:'Cormorant',serif;
  font-size:30px;font-weight:400;
  color:var(--ink);margin-bottom:18px;line-height:1.15;
}
.service-desc{
  font-size:14px;font-weight:300;line-height:1.9;
  color:var(--ink60);max-width:360px;
}
.service-arrow{
  display:inline-flex;align-items:center;gap:10px;
  margin-top:32px;font-size:11px;letter-spacing:2px;
  text-transform:uppercase;color:var(--emerald);
  transition:gap .35s;
}
.service-card:hover .service-arrow{gap:18px}

/* ── DOCTORS ── */
.doctors-section{background:#fff;border-top:1px solid var(--ink06)}
.doctors-header{
  display:flex;align-items:flex-end;justify-content:space-between;
  margin-bottom:72px;flex-wrap:wrap;gap:40px;
}
.doctors-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:32px}
.doctor-card{
  border:1px solid var(--ink06);border-radius:4px;
  overflow:hidden;background:#fff;
  transition:all .5s cubic-bezier(.16,1,.3,1);
}
.doctor-card:hover{
  transform:translateY(-10px);
  box-shadow:0 32px 80px rgba(0,0,0,.1);
  border-color:rgba(27,67,50,.2);
}
.doctor-img{height:340px;position:relative;overflow:hidden;background:var(--cream)}
.doctor-img img{transition:transform .7s ease}
.doctor-card:hover .doctor-img img{transform:scale(1.05)}
.doctor-badge{
  position:absolute;top:20px;left:20px;
  background:#fff;border:1px solid var(--ink06);
  padding:5px 14px;border-radius:100px;
  font-size:9px;letter-spacing:2px;text-transform:uppercase;
  color:var(--emerald);font-weight:500;
}
.doctor-body{padding:28px 32px 36px}
.doctor-name{
  font-family:'Cormorant',serif;
  font-size:24px;font-weight:400;
  color:var(--ink);margin-bottom:5px;
}
.doctor-role{font-size:13px;color:var(--ink60);font-weight:300;margin-bottom:18px}
.doctor-rating{display:flex;align-items:center;gap:10px;margin-bottom:24px}
.stars{color:var(--gold);font-size:12px;letter-spacing:2px}
.reviews{font-size:12px;color:var(--ink35)}
.btn-book{
  width:100%;
  background:transparent;
  border:1px solid var(--ink12);
  color:var(--ink60);
  padding:13px;
  font-size:10px;letter-spacing:2.5px;text-transform:uppercase;
  border-radius:2px;cursor:pointer;
  transition:all .3s;font-family:'Outfit',sans-serif;
}
.btn-book:hover{
  background:var(--emerald);border-color:var(--emerald);color:#fff;
}

/* ── TESTIMONIALS ── */
.test-section{background:var(--cream);border-top:1px solid var(--ink06)}
.test-header{display:flex;align-items:flex-end;justify-content:space-between;margin-bottom:72px;flex-wrap:wrap;gap:40px}
.test-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:28px}
.test-card{
  background:#fff;border:1px solid var(--ink06);
  border-radius:4px;padding:44px 40px;
  position:relative;transition:all .4s;
}
.test-card:hover{
  border-color:rgba(27,67,50,.15);
  box-shadow:0 20px 60px rgba(0,0,0,.07);
  transform:translateY(-4px);
}
.test-mark{
  font-family:'Cormorant',serif;
  font-size:72px;font-weight:300;line-height:.8;
  color:var(--emerald);opacity:.15;
  margin-bottom:20px;display:block;
}
.test-quote{
  font-family:'Cormorant',serif;
  font-size:17px;font-style:italic;font-weight:400;
  line-height:1.75;color:var(--ink);
  margin-bottom:32px;
}
.test-divider{height:1px;background:var(--ink06);margin-bottom:24px}
.test-author{display:flex;align-items:center;gap:14px}
.test-avatar{
  width:44px;height:44px;border-radius:50%;
  background:var(--emerald);
  display:flex;align-items:center;justify-content:center;
  font-family:'Cormorant',serif;font-size:18px;
  color:#fff;font-weight:400;flex-shrink:0;
}
.test-name{font-size:14px;font-weight:500;color:var(--ink);margin-bottom:3px}
.test-detail{font-size:12px;color:var(--ink35)}
.test-stars{color:var(--gold);font-size:11px;letter-spacing:2px;margin-top:4px}

/* ── FAQ ── */
.faq-section{background:#fff;border-top:1px solid var(--ink06)}
.faq-layout{display:grid;grid-template-columns:5fr 7fr;gap:120px;margin-top:80px;align-items:start}
.faq-sticky{position:sticky;top:100px}
.faq-aside{
  margin-top:52px;padding:44px;
  border:1px solid var(--ink06);border-radius:4px;
  background:var(--off);
}
.faq-aside-h{font-family:'Cormorant',serif;font-size:26px;font-weight:400;color:var(--ink);margin-bottom:14px}
.faq-aside-p{font-size:14px;font-weight:300;line-height:1.8;color:var(--ink60);margin-bottom:32px}
.faq-list{display:flex;flex-direction:column}
.faq-item{border-bottom:1px solid var(--ink06)}
.faq-q{
  display:flex;align-items:center;justify-content:space-between;
  padding:28px 0;gap:24px;
  font-family:'Cormorant',serif;
  font-size:20px;font-weight:400;
  color:var(--ink60);
  background:transparent;border:none;width:100%;text-align:left;
  cursor:pointer;transition:color .3s;
}
.faq-q:hover{color:var(--ink)}
.faq-item.open .faq-q{color:var(--ink)}
.faq-icon{
  width:28px;height:28px;border-radius:50%;
  border:1px solid var(--ink12);
  display:flex;align-items:center;justify-content:center;
  font-size:16px;color:var(--emerald);flex-shrink:0;
  transition:all .35s ease;
}
.faq-item.open .faq-icon{background:var(--emerald);color:#fff;transform:rotate(45deg);border-color:var(--emerald)}
.faq-a{
  font-size:15px;font-weight:300;line-height:1.85;
  color:var(--ink60);padding-bottom:28px;
  display:none;max-width:580px;
}
.faq-item.open .faq-a{display:block}

/* ── CTA SECTION ── */
.cta-section{
  position:relative;padding:0;
  display:grid;grid-template-columns:1fr 1fr;
  min-height:600px;overflow:hidden;
}
.cta-left{
  background:var(--emerald);
  padding:120px 80px;
  display:flex;flex-direction:column;justify-content:center;
  position:relative;overflow:hidden;
}
.cta-left::before{
  content:'';position:absolute;
  top:-100px;right:-100px;
  width:400px;height:400px;border-radius:50%;
  background:rgba(255,255,255,.04);
}
.cta-left::after{
  content:'';position:absolute;
  bottom:-60px;left:-60px;
  width:250px;height:250px;border-radius:50%;
  background:rgba(255,255,255,.03);
}
.cta-label{
  display:flex;align-items:center;gap:12px;
  font-size:10px;letter-spacing:3px;text-transform:uppercase;
  color:rgba(255,255,255,.6);margin-bottom:24px;
}
.cta-label::before{content:'';width:24px;height:1px;background:rgba(255,255,255,.4)}
.cta-h2{
  font-family:'Cormorant',serif;
  font-size:clamp(44px,4.5vw,72px);
  font-weight:300;line-height:1.08;
  color:#fff;margin-bottom:24px;letter-spacing:-.5px;
  position:relative;z-index:1;
}
.cta-h2 em{font-style:italic;color:var(--gold-lt)}
.cta-sub{
  font-size:15px;font-weight:300;line-height:1.8;
  color:rgba(255,255,255,.65);margin-bottom:52px;
  max-width:420px;position:relative;z-index:1;
}
.cta-btns{display:flex;flex-direction:column;gap:14px;position:relative;z-index:1}
.btn-cta-primary{
  display:inline-flex;align-items:center;gap:12px;
  padding:18px 44px;
  background:#fff;color:var(--emerald);
  font-size:11px;letter-spacing:2px;text-transform:uppercase;
  font-weight:600;border:none;cursor:pointer;border-radius:2px;
  transition:all .35s;align-self:flex-start;font-family:'Outfit',sans-serif;
}
.btn-cta-primary:hover{background:var(--cream);box-shadow:0 12px 40px rgba(0,0,0,.2)}
.btn-cta-secondary{
  display:inline-flex;align-items:center;gap:8px;
  font-size:11px;letter-spacing:2px;text-transform:uppercase;
  color:rgba(255,255,255,.6);background:transparent;border:none;
  cursor:pointer;transition:color .3s;font-family:'Outfit',sans-serif;
  align-self:flex-start;padding:0;
}
.btn-cta-secondary:hover{color:#fff}
.cta-right{position:relative;overflow:hidden}
.cta-right img{position:absolute;inset:0;width:100%;height:100%}
.cta-right-overlay{
  position:absolute;inset:0;
  background:linear-gradient(270deg,transparent 40%,rgba(27,67,50,.2) 100%);
}
.cta-right-pill{
  position:absolute;top:48px;right:48px;
  background:rgba(255,255,255,.95);backdrop-filter:blur(12px);
  border-radius:100px;padding:14px 24px;
  display:flex;align-items:center;gap:12px;
  border:1px solid var(--ink06);
}
.cta-pill-dot{width:8px;height:8px;border-radius:50%;background:#22c55e;box-shadow:0 0 0 3px rgba(34,197,94,.2)}
.cta-pill-text{font-size:12px;color:var(--ink);font-weight:500}

/* ── FOOTER ── */
.footer{background:var(--ink);padding:100px 60px 48px}
.footer-top{
  display:grid;grid-template-columns:2.2fr 1fr 1fr 1fr;
  gap:80px;padding-bottom:72px;
  border-bottom:1px solid rgba(255,255,255,.08);
  margin-bottom:48px;
}
.footer-brand{font-family:'Cormorant Garamond',serif;font-size:20px;letter-spacing:3px;text-transform:uppercase;color:#fff;margin-bottom:20px;font-weight:500}
.footer-desc{font-size:14px;font-weight:300;line-height:1.8;color:rgba(255,255,255,.35);max-width:280px;margin-bottom:36px}
.footer-badge{
  display:inline-flex;align-items:center;gap:8px;
  border:1px solid rgba(255,255,255,.12);border-radius:2px;
  padding:9px 18px;
  font-size:10px;letter-spacing:1.5px;text-transform:uppercase;
  color:rgba(255,255,255,.5);
}
.footer-col-h{font-size:10px;letter-spacing:2.5px;text-transform:uppercase;color:rgba(255,255,255,.3);margin-bottom:28px}
.footer-links{list-style:none;display:flex;flex-direction:column;gap:14px}
.footer-links a{font-size:14px;font-weight:300;color:rgba(255,255,255,.4);transition:color .25s}
.footer-links a:hover{color:rgba(255,255,255,.85)}
.footer-bottom{display:flex;align-items:flex-start;justify-content:space-between;gap:40px;flex-wrap:wrap}
.footer-legal{font-size:11px;font-weight:300;line-height:1.7;color:rgba(255,255,255,.2);max-width:680px}
.footer-copy{font-size:11px;letter-spacing:1px;color:rgba(255,255,255,.2);white-space:nowrap}

/* ── MODAL ── */
.modal-overlay{
  position:fixed;inset:0;z-index:500;
  background:rgba(13,13,13,.6);backdrop-filter:blur(16px);
  -webkit-backdrop-filter:blur(16px);
  display:flex;align-items:center;justify-content:center;
  padding:20px;animation:fadeIn .3s ease;
}
.modal{
  background:#fff;border-radius:4px;
  padding:64px;max-width:560px;width:100%;
  position:relative;
  animation:scaleIn .4s cubic-bezier(.16,1,.3,1);
  box-shadow:0 40px 120px rgba(0,0,0,.2);
}
.modal-close{
  position:absolute;top:24px;right:28px;
  background:transparent;border:none;
  color:var(--ink35);font-size:22px;cursor:pointer;
  width:36px;height:36px;border-radius:50%;
  display:flex;align-items:center;justify-content:center;
  transition:all .2s;
}
.modal-close:hover{background:var(--ink06);color:var(--ink)}
.modal-label{font-size:10px;letter-spacing:2.5px;text-transform:uppercase;color:var(--emerald);font-weight:500;margin-bottom:12px;display:flex;align-items:center;gap:10px}
.modal-label::before{content:'';width:20px;height:1px;background:var(--emerald)}
.modal-h{font-family:'Cormorant',serif;font-size:40px;font-weight:300;color:var(--ink);margin-bottom:8px;line-height:1.1}
.modal-sub{font-size:14px;font-weight:300;color:var(--ink60);margin-bottom:44px}
.modal-row{display:grid;grid-template-columns:1fr 1fr;gap:20px}
.field{margin-bottom:20px}
.field label{display:block;font-size:10px;letter-spacing:2px;text-transform:uppercase;color:var(--ink60);margin-bottom:10px;font-weight:500}
.field input,.field select{
  width:100%;background:#fff;
  border:1px solid var(--ink12);border-radius:2px;
  padding:14px 18px;font-size:14px;font-weight:300;
  color:var(--ink);outline:none;
  transition:border-color .3s;
  font-family:'Outfit',sans-serif;
}
.field input::placeholder{color:var(--ink35)}
.field input:focus,.field select:focus{border-color:var(--emerald);box-shadow:0 0 0 3px rgba(27,67,50,.08)}
.field select option{background:#fff}
.modal-submit{
  width:100%;margin-top:8px;
  background:var(--emerald);color:#fff;
  padding:17px;border:none;
  font-size:11px;letter-spacing:2.5px;text-transform:uppercase;
  font-weight:500;border-radius:2px;cursor:pointer;
  transition:all .3s;font-family:'Outfit',sans-serif;
}
.modal-submit:hover{background:var(--emerald-md);box-shadow:0 8px 32px rgba(27,67,50,.35)}
.modal-disclaimer{font-size:11px;color:var(--ink35);text-align:center;margin-top:16px;line-height:1.6}
.modal-success{text-align:center;padding:48px 0}
.modal-success-icon{font-size:52px;margin-bottom:24px;display:block}
.modal-success-h{font-family:'Cormorant',serif;font-size:34px;font-weight:300;color:var(--ink);margin-bottom:12px}
.modal-success-p{font-size:15px;font-weight:300;color:var(--ink60)}

/* ── RESPONSIVE ── */
@media(max-width:1200px){
  .nav{padding:0 40px}
  .section{padding:100px 40px}
  .section-sm{padding:80px 40px}
  .hero{grid-template-columns:1fr;padding-top:80px}
  .hero-left{padding:60px 40px 80px}
  .hero-right{display:none}
  .about-grid{grid-template-columns:1fr}
  .about-right{min-height:400px}
  .stats-band{grid-template-columns:1fr 1fr}
  .stat-item:nth-child(2){border-right:none}
  .stat-item:nth-child(3){border-top:1px solid rgba(255,255,255,.08)}
  .faq-layout{grid-template-columns:1fr;gap:60px}
  .faq-sticky{position:static}
  .cta-section{grid-template-columns:1fr}
  .cta-right{display:none}
  .cta-left{padding:100px 40px}
  .footer{padding:80px 40px 40px}
  .footer-top{grid-template-columns:1fr 1fr;gap:48px}
  .modal{padding:44px 36px}
}
@media(max-width:960px){
  .nav-links{display:none}
  .how-steps{grid-template-columns:1fr}
  .services-grid{grid-template-columns:1fr}
  .services-header{grid-template-columns:1fr}
  .how-header{grid-template-columns:1fr}
  .doctors-grid{grid-template-columns:1fr 1fr}
  .test-grid{grid-template-columns:1fr}
  .doctors-header{flex-direction:column;gap:24px}
}
@media(max-width:640px){
  .doctors-grid{grid-template-columns:1fr}
  .footer-top{grid-template-columns:1fr}
  .hero-h1{font-size:48px}
  .modal-row{grid-template-columns:1fr}
  .modal{padding:36px 24px}
  .stats-band{grid-template-columns:1fr}
  .stat-item{border-right:none;border-top:1px solid rgba(255,255,255,.08)}
  .stat-item:first-child{border-top:none}
  .about-left{padding:52px 36px}
}
`;

/* ── TRANSLATIONS ─────────────────────────────────────── */
const COPY = {
  IT: {
    nav: { links:[["services","Servizi"],["how","Come Funziona"],["doctors","Medici"],["faq","FAQ"]], cta:"Prenota Ora" },
    hero: {
      eyebrow: "Piattaforma di Telemedicina Certificata",
      h: ["Medicina", "di precisione", "per il tuo"],
      hEm: "benessere",
      sub: "KannavisCare è la prima piattaforma italiana di telemedicina specializzata nella cannabis terapeutica. Specialisti certificati, percorsi clinici personalizzati e consegna farmaceutica a domicilio.",
      cta1: "Prenota una Consulenza",
      cta2: "Scopri il Percorso",
      card: { n:"3.200", s:"+", label:"Pazienti in Cura", sub:"Protocolli clinici certificati" },
    },
    marquee: ["Prescrizioni Legali in Italia","Medici Abilitati SSN","Conformità GDPR","Prima visita entro 48 ore","Consegna Farmaceutica Certificata","Riservatezza Assoluta"],
    about: {
      label:"Chi Siamo",
      h:["Una nuova era della"],hEm:"medicina integrativa",
      p1:"KannavisCare nasce dalla volontà di rendere accessibile la cannabis terapeutica a chi ne ha realmente bisogno, attraverso un percorso medico serio, certificato e completamente digitale.",
      p2:"Il nostro approccio integra le più recenti evidenze cliniche internazionali con l'eccellenza della medicina italiana, nel pieno rispetto delle normative vigenti.",
      certs:["Certificato dal Ministero della Salute","Conformità GDPR — Dati cifrati AES-256","Medici iscritti all'Ordine dei Medici"],
      badge:{ n:"2017", text:"Anno di fondazione · Certificati dal MSP" },
    },
    stats:[
      { n:"3.200", s:"+", label:"Pazienti in Cura", sub:"Con protocolli clinici certificati" },
      { n:"48", s:"h", label:"Alla Prima Visita", sub:"Dalla registrazione alla consulenza" },
      { n:"98", s:"%", label:"Soddisfazione", sub:"Valutato dai pazienti" },
      { n:"12", s:"+", label:"Specialità Mediche", sub:"Trattate dalla piattaforma" },
    ],
    how: {
      label:"Come Funziona",h:["Tre passi verso"],hEm:"la guarigione",
      lead:"Un percorso medico strutturato e certificato, progettato per adattarsi alla tua vita.",
      steps:[
        { n:"01 — Valutazione", img:IMG.step1, title:"Questionario Medico", desc:"Un questionario clinico anonimo raccoglie la tua storia medica e le tue esigenze. Sicuro, riservato e completamente online. Meno di 5 minuti.", cta:"Inizia la valutazione" },
        { n:"02 — Consulenza", img:IMG.step2, title:"Visita Telematica", desc:"Il nostro specialista conduce una visita video certificata in totale riservatezza. Dal tuo dispositivo, senza code o spostamenti, in meno di 48 ore.", cta:"Scopri la piattaforma" },
        { n:"03 — Trattamento", img:IMG.step3, title:"Prescrizione & Consegna", desc:"Se clinicamente idoneo, ricevi la prescrizione medica certificata e il trattamento farmaceutico direttamente a domicilio in packaging discreto.", cta:"Prenota ora" },
      ]
    },
    services: {
      label:"I Nostri Servizi",h:["Specializzazioni"],hEm:"cliniche",
      lead:"Ogni indicazione terapeutica è trattata da specialisti dedicati, con protocolli evidence-based aggiornati alle più recenti linee guida internazionali.",
      items:[
        { n:"01", tag:"Neurologia", title:"Dolore Cronico & Neurologico", desc:"Protocolli avanzati per la gestione del dolore cronico, neuropatie periferiche, fibromialgia e condizioni neuroinfiammatorie refrattarie ad altre terapie farmacologiche.", cta:"Scopri il protocollo" },
        { n:"02", tag:"Psichiatria", title:"Disturbi del Sonno & Ansia", desc:"Approcci clinici integrati per insonnia cronica, disturbo d'ansia generalizzato, PTSD e condizioni correlate allo stress cronico, con valutazione psichiatrica specialistica.", cta:"Scopri il protocollo" },
        { n:"03", tag:"Oncologia di Supporto", title:"Supporto Oncologico", desc:"Terapie complementari certificate per la gestione della nausea, anoressia e dolore correlati alle terapie oncologiche, in affiancamento all'oncologo curante.", cta:"Scopri il protocollo" },
        { n:"04", tag:"Neurologia", title:"Epilessia & Spasticità", desc:"Trattamenti farmacologici consolidati e validati per la riduzione delle crisi epilettiche farmacoresistenti e la gestione clinica della spasticità muscolare.", cta:"Scopri il protocollo" },
      ]
    },
    doctors: {
      label:"Il Nostro Team",h:["Medici di"],hEm:"eccellenza",
      lead:"Ogni specialista è selezionato per rigore clinico, esperienza nel campo della medicina integrativa e approccio empatico al paziente.",
      items:[
        { img:IMG.doctor1, badge:"Neurologia", name:"Dott. Marco Ferretti", role:"Neurologo — Università degli Studi di Milano", stars:"★★★★★", reviews:"124 recensioni verificate" },
        { img:IMG.doctor2, badge:"Psichiatria", name:"Dott.ssa Elena Russo", role:"Psichiatra — Università La Sapienza, Roma", stars:"★★★★★", reviews:"98 recensioni verificate" },
        { img:IMG.doctor3, badge:"Oncologia", name:"Dott. Luca Bianchi", role:"Oncologo — Università di Bologna", stars:"★★★★★", reviews:"156 recensioni verificate" },
      ],
      book:"Prenota Visita",
    },
    testimonials: {
      label:"Testimonianze",h:["Storie di"],hEm:"guarigione",
      lead:"Ogni paziente è una storia. Queste sono alcune delle esperienze condivise con la nostra piattaforma.",
      items:[
        { text:"Dopo anni di dolore cronico e terapie inefficaci, KannavisCare mi ha offerto un percorso medico serio e rispettoso. La mia qualità di vita è cambiata in modo radicale. Non avrei mai creduto fosse possibile.", initials:"M.T.", detail:"Paziente, Dolore Cronico · Milano" },
        { text:"Processo semplice, medici di altissimo livello e totale riservatezza. Finalmente una piattaforma che tratta questo tema con la serietà medica che merita. Consiglio a chiunque stia valutando questa opzione.", initials:"R.C.", detail:"Paziente, Disturbi del Sonno · Roma" },
        { text:"Ho potuto accedere alle cure senza spostarmi dalla mia città. Un servizio che mancava in Italia, gestito con una professionalità che mi ha sorpreso e rassicurato dall'inizio alla fine.", initials:"A.L.", detail:"Paziente, Ansia · Napoli" },
      ]
    },
    faq: {
      label:"FAQ",h:["Domande"],hEm:"frequenti",
      aside:{ h:"Hai altri dubbi?", p:"Il nostro team medico è disponibile a rispondere a qualsiasi domanda prima di intraprendere il percorso terapeutico.", cta:"Contattaci Ora" },
      items:[
        { q:"La cannabis terapeutica è legale in Italia?", a:"Sì. In Italia la cannabis terapeutica è legale con prescrizione medica dal 2007 (D.M. 18/04/2007). Il successivo Decreto Ministeriale del 2015 ha ampliato significativamente le indicazioni cliniche autorizzate, includendo dolore cronico, spasticità, nausea e vomito da chemioterapia, sindrome di Tourette e disturbi del sonno." },
        { q:"Quanto costa la prima consulenza?", a:"La prima visita telematica parte da €79, comprensiva di valutazione clinica completa da parte di uno specialista e, se clinicamente idoneo, della relativa prescrizione medica. I prodotti farmaceutici hanno un costo separato che varia in base al trattamento prescritto e alla posologia indicata." },
        { q:"Quanto devo attendere per la prima visita?", a:"La nostra piattaforma garantisce la prima consulenza entro 48 ore dalla prenotazione. In media, i nostri pazienti vengono visitati entro 24 ore. Su richiesta gestiamo anche casi con necessità di accesso più rapido." },
        { q:"Come vengono protetti i miei dati sanitari?", a:"Siamo pienamente conformi al GDPR e alle normative italiane in materia di privacy dei dati sanitari (D.Lgs. 196/2003 e s.m.i.). Tutti i dati sono cifrati con protocolli AES-256, archiviati su server certificati ISO 27001 localizzati in Europa e non vengono mai condivisi con soggetti terzi." },
        { q:"Posso ricevere il trattamento direttamente a domicilio?", a:"Sì. Una volta emessa la prescrizione medica, il prodotto farmaceutico viene consegnato direttamente al domicilio indicato tramite farmacia convenzionata certificata, in packaging anonimo e sicuro, con rigoroso rispetto della catena del freddo ove clinicamente necessario." },
        { q:"La piattaforma è accessibile da tutta Italia?", a:"Sì. KannavisCare è accessibile da qualsiasi regione italiana. La telemedicina consente di effettuare la visita e ottenere la prescrizione indipendentemente dalla propria posizione geografica, eliminando la necessità di spostamenti presso centri specialistici." },
      ]
    },
    cta: {
      label:"Inizia il Percorso",
      h:["Inizia il tuo"],hEm:"percorso clinico",
      sub:"Prima consulenza garantita entro 48 ore. Medici specialisti certificati. Riservatezza assoluta e conformità GDPR.",
      cta1:"Prenota la Prima Visita",cta2:"Parla con un Medico",
      pill:"Medici disponibili adesso",
    },
    modal: {
      label:"Prenota la Consulenza",h:"Inizia il Percorso",sub:"Inserisci i tuoi dati per prenotare la prima visita con uno specialista",
      name:"Nome",surname:"Cognome",email:"Indirizzo Email",phone:"Telefono (opzionale)",
      condition:"Indicazione Principale",
      conditions:["Seleziona la condizione...","Dolore Cronico","Disturbi del Sonno","Ansia / Stress / PTSD","Supporto Oncologico","Epilessia / Spasticità","Altro"],
      cta:"Prenota la Consulenza →",
      disclaimer:"I tuoi dati sono protetti dal GDPR · Mai condivisi con terzi · Risposta entro 24 ore",
    },
    footer: {
      desc:"La prima piattaforma italiana di telemedicina per la cannabis terapeutica. Medici specializzati, percorsi clinici certificati e riservatezza assoluta.",
      badge:"🔒 Conforme al GDPR",
      cols:[
        { h:"Servizi", links:["Dolore Cronico","Disturbi del Sonno","Supporto Oncologico","Epilessia & Spasticità"] },
        { h:"Azienda", links:["Chi Siamo","I Nostri Medici","Press & Media","Lavora con Noi"] },
        { h:"Supporto", links:["FAQ","Contattaci","Privacy Policy","Cookie Policy"] },
      ],
      legal:"* KannavisCare S.r.l. opera come piattaforma di telemedicina ai sensi del D.Lgs. 502/1992 e s.m.i. Le prescrizioni sono emesse esclusivamente da medici iscritti all'Ordine dei Medici Chirurghi e degli Odontoiatri abilitati alla pratica medica in Italia. La cannabis terapeutica è prescrivibile esclusivamente a fronte di specifica indicazione clinica valutata dal medico specialista. I prodotti sono dispensati esclusivamente da farmacie autorizzate ai sensi della normativa vigente.",
      copy:"© 2025 KannavisCare S.r.l. — P.IVA IT12345678901",
    },
    success: { h:"Richiesta Ricevuta", p:"Ti contatteremo entro 24 ore per confermare la tua consulenza." },
  }
};

COPY.EN = {
  nav:{ links:[["services","Services"],["how","How It Works"],["doctors","Doctors"],["faq","FAQ"]], cta:"Book Now" },
  hero:{
    eyebrow:"Certified Telemedicine Platform",
    h:["Precision","medicine","for your"],hEm:"wellbeing",
    sub:"KannavisCare is Italy's first telemedicine platform specialized in therapeutic cannabis. Certified specialists, personalized clinical pathways and pharmaceutical home delivery.",
    cta1:"Book a Consultation",cta2:"Discover the Journey",
    card:{ n:"3,200", s:"+", label:"Patients in Care", sub:"Certified clinical protocols" },
  },
  marquee:["Legal Prescriptions in Italy","NHS-Certified Physicians","GDPR Compliant","First visit within 48 hours","Certified Pharmaceutical Delivery","Total Confidentiality"],
  about:{
    label:"About Us",h:["A new era of"],hEm:"integrative medicine",
    p1:"KannavisCare was founded with the mission of making therapeutic cannabis accessible to those who truly need it, through a serious, certified, and fully digital medical pathway.",
    p2:"Our approach integrates the latest international clinical evidence with Italian medical excellence, in full compliance with current regulations.",
    certs:["Certified by the Ministry of Health","GDPR Compliant — AES-256 encrypted data","Physicians registered with the Medical Association"],
    badge:{ n:"2017", text:"Year of foundation · Certified by MoH" },
  },
  stats:[
    { n:"3,200", s:"+", label:"Patients in Care", sub:"With certified clinical protocols" },
    { n:"48", s:"h", label:"To First Visit", sub:"From registration to consultation" },
    { n:"98", s:"%", label:"Satisfaction Rate", sub:"Rated by our patients" },
    { n:"12", s:"+", label:"Medical Specialties", sub:"Treated on the platform" },
  ],
  how:{
    label:"How It Works",h:["Three steps to"],hEm:"recovery",
    lead:"A structured and certified medical pathway, designed to fit your life.",
    steps:[
      { n:"01 — Assessment", img:IMG.step1, title:"Medical Questionnaire", desc:"An anonymous clinical questionnaire collects your medical history and needs. Secure, confidential and completely online. Less than 5 minutes.", cta:"Start assessment" },
      { n:"02 — Consultation", img:IMG.step2, title:"Telemedicine Visit", desc:"Our specialist conducts a certified video consultation in total confidentiality. From your device, without queues or travel, within 48 hours.", cta:"Explore the platform" },
      { n:"03 — Treatment", img:IMG.step3, title:"Prescription & Delivery", desc:"If clinically eligible, receive your certified medical prescription and pharmaceutical treatment directly to your door in discreet packaging.", cta:"Book now" },
    ]
  },
  services:{
    label:"Our Services",h:["Clinical"],hEm:"specializations",
    lead:"Each therapeutic indication is handled by dedicated specialists, with evidence-based protocols updated to the latest international guidelines.",
    items:[
      { n:"01", tag:"Neurology", title:"Chronic & Neurological Pain", desc:"Advanced protocols for chronic pain management, peripheral neuropathies, fibromyalgia and neuroinflammatory conditions refractory to other pharmacological therapies.", cta:"Discover protocol" },
      { n:"02", tag:"Psychiatry", title:"Sleep Disorders & Anxiety", desc:"Integrated clinical approaches for chronic insomnia, generalized anxiety disorder, PTSD and chronic stress-related conditions, with specialist psychiatric evaluation.", cta:"Discover protocol" },
      { n:"03", tag:"Oncology Support", title:"Oncological Support", desc:"Certified complementary therapies for managing nausea, anorexia and pain related to oncological therapies, alongside the treating oncologist.", cta:"Discover protocol" },
      { n:"04", tag:"Neurology", title:"Epilepsy & Spasticity", desc:"Consolidated and validated pharmacological treatments for reducing drug-resistant epileptic seizures and clinical management of muscle spasticity.", cta:"Discover protocol" },
    ]
  },
  doctors:{
    label:"Our Team",h:["Physicians of"],hEm:"excellence",
    lead:"Every specialist is selected for clinical rigor, experience in integrative medicine and empathetic patient approach.",
    items:[
      { img:IMG.doctor1, badge:"Neurology", name:"Dr. Marco Ferretti", role:"Neurologist — University of Milan", stars:"★★★★★", reviews:"124 verified reviews" },
      { img:IMG.doctor2, badge:"Psychiatry", name:"Dr. Elena Russo", role:"Psychiatrist — La Sapienza University, Rome", stars:"★★★★★", reviews:"98 verified reviews" },
      { img:IMG.doctor3, badge:"Oncology", name:"Dr. Luca Bianchi", role:"Oncologist — University of Bologna", stars:"★★★★★", reviews:"156 verified reviews" },
    ],
    book:"Book Visit",
  },
  testimonials:{
    label:"Testimonials",h:["Stories of"],hEm:"recovery",
    lead:"Every patient is a story. These are some of the experiences shared with our platform.",
    items:[
      { text:"After years of chronic pain and ineffective therapies, KannavisCare offered me a serious and respectful medical pathway. My quality of life has changed radically. I never thought it was possible.", initials:"M.T.", detail:"Patient, Chronic Pain · Milan" },
      { text:"Simple process, highest-level doctors and total confidentiality. Finally a platform that treats this topic with the medical seriousness it deserves. I recommend it to anyone considering this option.", initials:"R.C.", detail:"Patient, Sleep Disorders · Rome" },
      { text:"I was able to access treatment without leaving my city. A service Italy was missing, managed with a professionalism that surprised and reassured me from start to finish.", initials:"A.L.", detail:"Patient, Anxiety · Naples" },
    ]
  },
  faq:{
    label:"FAQ",h:["Frequently"],hEm:"asked questions",
    aside:{ h:"More questions?", p:"Our medical team is available to answer any question before starting the therapeutic pathway.", cta:"Contact Us Now" },
    items:[
      { q:"Is therapeutic cannabis legal in Italy?", a:"Yes. Therapeutic cannabis has been legal in Italy with a medical prescription since 2007 (D.M. 18/04/2007). The subsequent 2015 Ministerial Decree significantly expanded the authorized clinical indications, including chronic pain, spasticity, nausea and vomiting from chemotherapy, Tourette syndrome and sleep disorders." },
      { q:"How much does the first consultation cost?", a:"The first telemedicine visit starts from €79, including a complete clinical evaluation by a specialist and, if clinically eligible, the related medical prescription. Pharmaceutical products have a separate cost that varies based on the prescribed treatment and indicated dosage." },
      { q:"How long do I have to wait for the first visit?", a:"Our platform guarantees the first consultation within 48 hours of booking. On average, our patients are seen within 24 hours. Upon request, we also manage cases requiring faster access." },
      { q:"How is my health data protected?", a:"We fully comply with GDPR and Italian health data privacy regulations. All data is encrypted with AES-256 protocols, stored on ISO 27001 certified servers located in Europe and never shared with third parties." },
      { q:"Can I receive the treatment directly at home?", a:"Yes. Once the medical prescription is issued, the pharmaceutical product is delivered directly to the indicated address via a certified partnered pharmacy, in anonymous and secure packaging, with strict adherence to the cold chain where clinically necessary." },
      { q:"Is the platform accessible from all of Italy?", a:"Yes. KannavisCare is accessible from any Italian region. Telemedicine allows you to complete the visit and obtain the prescription regardless of your geographic location, eliminating the need to travel to specialist centers." },
    ]
  },
  cta:{
    label:"Start Your Journey",h:["Start your"],hEm:"clinical pathway",
    sub:"First consultation guaranteed within 48 hours. Certified specialist physicians. Absolute confidentiality and GDPR compliance.",
    cta1:"Book First Visit",cta2:"Talk to a Doctor",
    pill:"Doctors available now",
  },
  modal:{
    label:"Book Consultation",h:"Begin Your Journey",sub:"Enter your details to book your first appointment with a specialist",
    name:"First Name",surname:"Last Name",email:"Email Address",phone:"Phone (optional)",
    condition:"Primary Indication",
    conditions:["Select condition...","Chronic Pain","Sleep Disorders","Anxiety / Stress / PTSD","Oncological Support","Epilepsy / Spasticity","Other"],
    cta:"Book Consultation →",
    disclaimer:"Your data is GDPR protected · Never shared with third parties · Response within 24 hours",
  },
  footer:{
    desc:"Italy's first telemedicine platform for therapeutic cannabis. Specialist physicians, certified clinical pathways and absolute confidentiality.",
    badge:"🔒 GDPR Compliant",
    cols:[
      { h:"Services", links:["Chronic Pain","Sleep Disorders","Oncological Support","Epilepsy & Spasticity"] },
      { h:"Company", links:["About Us","Our Doctors","Press & Media","Careers"] },
      { h:"Support", links:["FAQ","Contact Us","Privacy Policy","Cookie Policy"] },
    ],
    legal:"* KannavisCare S.r.l. operates as a telemedicine platform. Prescriptions are issued exclusively by physicians registered with the Italian Medical Association and licensed to practice medicine in Italy. Therapeutic cannabis is prescribable only in the presence of specific clinical indications assessed by the specialist physician. Products are dispensed exclusively by authorized pharmacies in accordance with current regulations.",
    copy:"© 2025 KannavisCare S.r.l. — VAT IT12345678901",
  },
  success:{ h:"Request Received", p:"We will contact you within 24 hours to confirm your consultation." },
};

/* ── COMPONENT ────────────────────────────────────────── */
export default function KannavisCare() {
  const [lang, setLang] = useState("IT");
  const [scrolled, setScrolled] = useState(false);
  const [modal, setModal] = useState(false);
  const [faqOpen, setFaqOpen] = useState(null);
  const [form, setForm] = useState({ name:"", surname:"", email:"", phone:"", condition:"" });
  const [done, setDone] = useState(false);
  const heroImgRef = useRef(null);
  const L = COPY[lang];

  // Scroll listener
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  // Hero image parallax load
  useEffect(() => {
    const t = setTimeout(() => {
      heroImgRef.current?.classList.add("loaded");
    }, 80);
    return () => clearTimeout(t);
  }, []);

  // Lock body scroll when modal open
  useEffect(() => {
    document.body.style.overflow = modal ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [modal]);

  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  const openModal = () => setModal(true);
  const closeModal = () => setModal(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setDone(true);
    setTimeout(() => {
      closeModal();
      setTimeout(() => { setDone(false); setForm({ name:"", surname:"", email:"", phone:"", condition:"" }); }, 400);
    }, 2600);
  };

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <>
      <style>{CSS}</style>

      {/* ── NAV ── */}
      <nav className={`nav${scrolled ? " solid" : ""}`} role="navigation" aria-label="Main navigation">
        <a href="/" className="nav-logo" aria-label="KannavisCare homepage">
          <div className="nav-logo-icon" aria-hidden="true">KC</div>
          KannavisCare
        </a>
        <ul className="nav-links">
          {L.nav.links.map(([id, lbl]) => (
            <li key={id}>
              <a href={`#${id}`} onClick={e => { e.preventDefault(); go(id); }}>{lbl}</a>
            </li>
          ))}
        </ul>
        <div className="nav-right">
          <div className="lang-switch" role="group" aria-label="Language selection">
            <button className={lang === "IT" ? "on" : ""} onClick={() => setLang("IT")} aria-pressed={lang === "IT"}>IT</button>
            <button className={lang === "EN" ? "on" : ""} onClick={() => setLang("EN")} aria-pressed={lang === "EN"}>EN</button>
          </div>
          <button className="nav-book" onClick={openModal}>{L.nav.cta}</button>
        </div>
      </nav>

      {/* ── HERO ── */}
      <header className="hero" role="banner" id="home">
        <div className="hero-left">
          <div className="hero-eyebrow">
            <div className="hero-eyebrow-line" aria-hidden="true" />
            <span className="hero-eyebrow-text">{L.hero.eyebrow}</span>
          </div>
          <h1 className="hero-h1">
            {L.hero.h[0]}<br />
            {L.hero.h[1]}<br />
            {L.hero.h[2]} <em>{L.hero.hEm}</em>
          </h1>
          <p className="hero-sub">{L.hero.sub}</p>
          <div className="hero-actions">
            <button className="btn-primary" onClick={openModal}>
              <span>{L.hero.cta1}</span>
              <i aria-hidden="true">→</i>
            </button>
            <button className="btn-secondary" onClick={() => go("how")}>
              {L.hero.cta2}
            </button>
          </div>
        </div>

        <div className="hero-right">
          <div ref={heroImgRef} className="hero-right-img">
            <img src={IMG.hero} alt="Medical consultation" loading="eager" />
          </div>
          <div className="hero-right-overlay" aria-hidden="true" />
          <div className="hero-card" aria-label="Platform statistics">
            <div className="hero-card-num">{L.hero.card.n}<span>{L.hero.card.s}</span></div>
            <div className="hero-card-label">{L.hero.card.label}</div>
            <div className="hero-card-sub">{L.hero.card.sub}</div>
          </div>
        </div>

        <div className="hero-scroll" aria-hidden="true">
          <span>scroll</span>
          <div className="hero-scroll-track">
            <div className="hero-scroll-dot" style={{ height: "24px" }} />
          </div>
        </div>
      </header>

      {/* ── MARQUEE ── */}
      <div className="marquee-strip" aria-hidden="true">
        <div className="marquee-inner">
          {[...L.marquee, ...L.marquee, ...L.marquee, ...L.marquee].map((item, i) => (
            <div className="marquee-item" key={i}>
              <span className="marquee-dot" />
              {item}
            </div>
          ))}
        </div>
      </div>

      {/* ── ABOUT ── */}
      <section className="section" id="about" aria-labelledby="about-h">
        <div className="s-label">{L.about.label}</div>
        <div className="about-grid">
          <div className="about-left">
            <h2 className="s-h2" id="about-h">
              {L.about.h[0]}<br /><em>{L.about.hEm}</em>
            </h2>
            <p className="about-p">{L.about.p1}</p>
            <p className="about-p" style={{ marginTop: 0 }}>{L.about.p2}</p>
            <div className="about-certifications" role="list" aria-label="Certifications">
              {L.about.certs.map((c, i) => (
                <div key={i} className="about-cert" role="listitem">
                  <div className="about-cert-icon" aria-hidden="true">✓</div>
                  {c}
                </div>
              ))}
            </div>
          </div>
          <div className="about-right">
            <img src={IMG.about} alt="Medical wellness" loading="lazy" />
            <div className="about-badge">
              <div className="about-badge-num">{L.about.badge.n}</div>
              <div className="about-badge-text">{L.about.badge.text}</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── STATS ── */}
      <div className="stats-band" role="region" aria-label="Platform statistics">
        {L.stats.map((s, i) => (
          <div key={i} className="stat-item">
            <div className="stat-n">{s.n}<span>{s.s}</span></div>
            <div className="stat-label">{s.label}</div>
            <div className="stat-sub">{s.sub}</div>
          </div>
        ))}
      </div>

      {/* ── HOW IT WORKS ── */}
      <section className="section how-section" id="how" aria-labelledby="how-h">
        <div className="how-header">
          <div>
            <div className="s-label">{L.how.label}</div>
            <h2 className="s-h2" id="how-h">{L.how.h[0]}<br /><em>{L.how.hEm}</em></h2>
          </div>
          <p className="s-lead">{L.how.lead}</p>
        </div>
        <div className="how-steps" role="list">
          {L.how.steps.map((s, i) => (
            <article key={i} className="how-step" role="listitem">
              <div className="how-step-img">
                <img src={s.img} alt={s.title} loading="lazy" />
                <div className="how-step-img-overlay" aria-hidden="true" />
              </div>
              <div className="how-step-body">
                <div className="how-step-num">{s.n}</div>
                <h3 className="how-step-title">{s.title}</h3>
                <p className="how-step-desc">{s.desc}</p>
                <button className="how-step-cta" onClick={openModal} aria-label={s.cta}>
                  {s.cta} →
                </button>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* ── SERVICES ── */}
      <section className="section services-section" id="services" aria-labelledby="svc-h">
        <div className="services-header">
          <div>
            <div className="s-label">{L.services.label}</div>
            <h2 className="s-h2" id="svc-h">{L.services.h[0]}<br /><em>{L.services.hEm}</em></h2>
          </div>
          <p className="s-lead">{L.services.lead}</p>
        </div>
        <div className="services-grid" role="list">
          {L.services.items.map((s, i) => (
            <article
              key={i} className="service-card" role="listitem"
              tabIndex={0}
              onClick={openModal}
              onKeyDown={e => e.key === "Enter" && openModal()}
              aria-label={`Service: ${s.title}`}
            >
              <div className="service-num" aria-hidden="true">{s.n}</div>
              <div className="service-tag">{s.tag}</div>
              <h3 className="service-title">{s.title}</h3>
              <p className="service-desc">{s.desc}</p>
              <div className="service-arrow" aria-hidden="true">{s.cta} →</div>
            </article>
          ))}
        </div>
      </section>

      {/* ── DOCTORS ── */}
      <section className="section doctors-section" id="doctors" aria-labelledby="doc-h">
        <div className="doctors-header">
          <div>
            <div className="s-label">{L.doctors.label}</div>
            <h2 className="s-h2" id="doc-h">{L.doctors.h[0]}<br /><em>{L.doctors.hEm}</em></h2>
          </div>
          <p className="s-lead" style={{ maxWidth: 380 }}>{L.doctors.lead}</p>
        </div>
        <div className="doctors-grid" role="list">
          {L.doctors.items.map((d, i) => (
            <article key={i} className="doctor-card" role="listitem">
              <div className="doctor-img">
                <img src={d.img} alt={d.name} loading="lazy" />
                <span className="doctor-badge">{d.badge}</span>
              </div>
              <div className="doctor-body">
                <h3 className="doctor-name">{d.name}</h3>
                <p className="doctor-role">{d.role}</p>
                <div className="doctor-rating">
                  <span className="stars" aria-label="5 out of 5 stars">{d.stars}</span>
                  <span className="reviews">{d.reviews}</span>
                </div>
                <button className="btn-book" onClick={openModal} aria-label={`${L.doctors.book}: ${d.name}`}>
                  {L.doctors.book}
                </button>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section className="section test-section" aria-labelledby="test-h">
        <div className="test-header">
          <div>
            <div className="s-label">{L.testimonials.label}</div>
            <h2 className="s-h2" id="test-h">{L.testimonials.h[0]}<br /><em>{L.testimonials.hEm}</em></h2>
          </div>
          <p className="s-lead" style={{ maxWidth: 380 }}>{L.testimonials.lead}</p>
        </div>
        <div className="test-grid" role="list">
          {L.testimonials.items.map((t, i) => (
            <article key={i} className="test-card" role="listitem">
              <span className="test-mark" aria-hidden="true">"</span>
              <blockquote className="test-quote">"{t.text}"</blockquote>
              <div className="test-divider" aria-hidden="true" />
              <div className="test-author">
                <div className="test-avatar" aria-hidden="true">{t.initials[0]}</div>
                <div>
                  <div className="test-name">{t.initials}</div>
                  <div className="test-detail">{t.detail}</div>
                  <div className="test-stars" aria-label="5 stars">★★★★★</div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="section faq-section" id="faq" aria-labelledby="faq-h">
        <div className="s-label">{L.faq.label}</div>
        <h2 className="s-h2" id="faq-h">{L.faq.h[0]}<br /><em>{L.faq.hEm}</em></h2>
        <div className="faq-layout">
          <div className="faq-sticky">
            <p className="s-lead">{L.faq.aside.p}</p>
            <div className="faq-aside">
              <div className="faq-aside-h">{L.faq.aside.h}</div>
              <p className="faq-aside-p">{L.faq.aside.p}</p>
              <button className="btn-primary" onClick={openModal}>
                <span>{L.faq.aside.cta}</span>
                <i aria-hidden="true">→</i>
              </button>
            </div>
          </div>
          <div className="faq-list">
            {L.faq.items.map((item, i) => (
              <div key={i} className={`faq-item${faqOpen === i ? " open" : ""}`}>
                <button
                  className="faq-q"
                  onClick={() => setFaqOpen(faqOpen === i ? null : i)}
                  aria-expanded={faqOpen === i}
                  aria-controls={`faq-a-${i}`}
                >
                  {item.q}
                  <span className="faq-icon" aria-hidden="true">+</span>
                </button>
                <div className="faq-a" id={`faq-a-${i}`} aria-hidden={faqOpen !== i}>
                  {item.a}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="cta-section" aria-labelledby="cta-h">
        <div className="cta-left">
          <div className="cta-label">{L.cta.label}</div>
          <h2 className="cta-h2" id="cta-h">
            {L.cta.h[0]}<br /><em>{L.cta.hEm}</em>
          </h2>
          <p className="cta-sub">{L.cta.sub}</p>
          <div className="cta-btns">
            <button className="btn-cta-primary" onClick={openModal}>{L.cta.cta1} →</button>
            <button className="btn-cta-secondary" onClick={() => go("doctors")}>{L.cta.cta2}</button>
          </div>
        </div>
        <div className="cta-right" aria-hidden="true">
          <img src={IMG.cta} alt="" loading="lazy" />
          <div className="cta-right-overlay" />
          <div className="cta-right-pill">
            <div className="cta-pill-dot" />
            <span className="cta-pill-text">{L.cta.pill}</span>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="footer" role="contentinfo">
        <div className="footer-top">
          <div>
            <div className="footer-brand">KannavisCare</div>
            <p className="footer-desc">{L.footer.desc}</p>
            <div className="footer-badge">{L.footer.badge}</div>
          </div>
          {L.footer.cols.map((col, i) => (
            <div key={i}>
              <div className="footer-col-h">{col.h}</div>
              <ul className="footer-links">
                {col.links.map((lnk, j) => (
                  <li key={j}><a href="#" onClick={e => e.preventDefault()}>{lnk}</a></li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="footer-bottom">
          <p className="footer-legal">{L.footer.legal}</p>
          <div className="footer-copy">{L.footer.copy}</div>
        </div>
      </footer>

      {/* ── MODAL ── */}
      {modal && (
        <div
          className="modal-overlay"
          role="dialog" aria-modal="true" aria-labelledby="modal-h"
          onClick={e => e.target === e.currentTarget && closeModal()}
        >
          <div className="modal">
            <button className="modal-close" onClick={closeModal} aria-label="Close modal">✕</button>

            {done ? (
              <div className="modal-success">
                <span className="modal-success-icon" aria-hidden="true">✅</span>
                <h3 className="modal-success-h">{L.success.h}</h3>
                <p className="modal-success-p">{L.success.p}</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate>
                <div className="modal-label">{L.modal.label}</div>
                <h2 className="modal-h" id="modal-h">{L.modal.h}</h2>
                <p className="modal-sub">{L.modal.sub}</p>

                <div className="modal-row">
                  <div className="field">
                    <label htmlFor="m-name">{L.modal.name}</label>
                    <input id="m-name" type="text" required placeholder="Marco" value={form.name} onChange={set("name")} aria-required="true" />
                  </div>
                  <div className="field">
                    <label htmlFor="m-sur">{L.modal.surname}</label>
                    <input id="m-sur" type="text" required placeholder="Rossi" value={form.surname} onChange={set("surname")} aria-required="true" />
                  </div>
                </div>

                <div className="field">
                  <label htmlFor="m-email">{L.modal.email}</label>
                  <input id="m-email" type="email" required placeholder="marco@email.com" value={form.email} onChange={set("email")} aria-required="true" />
                </div>

                <div className="field">
                  <label htmlFor="m-phone">{L.modal.phone}</label>
                  <input id="m-phone" type="tel" placeholder="+39 333 000 0000" value={form.phone} onChange={set("phone")} />
                </div>

                <div className="field">
                  <label htmlFor="m-cond">{L.modal.condition}</label>
                  <select id="m-cond" required value={form.condition} onChange={set("condition")} aria-required="true">
                    {L.modal.conditions.map((c, i) => (
                      <option key={i} value={i === 0 ? "" : c}>{c}</option>
                    ))}
                  </select>
                </div>

                <button type="submit" className="modal-submit">{L.modal.cta}</button>
                <p className="modal-disclaimer">🔒 {L.modal.disclaimer}</p>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
